import {
  type Initiative,
  type InitiativeImpactSeries,
  MonthlyImpactMap,
} from "aint-types";
import dayjs, { Dayjs } from "dayjs";
import quarterofyear from "dayjs/plugin/quarterOfYear";
import type { FieldError, Path } from "react-hook-form";

import { getMissingImpactValues } from "../app/utils";

dayjs.extend(quarterofyear);

export type FormFieldErrors = {
  [key in Path<Initiative>]?: FieldError;
};

export const REQUIRED_ERROR = {
  type: "required",
  message: "This field is required",
} satisfies FieldError;

function getMaxLengthError(fieldName: string, maxLength: number) {
  return {
    type: "maxLength",
    message: `The ${fieldName} must be no longer than ${maxLength} characters.`,
  };
}

export function validateFormFieldsForDraft(formFields: Partial<Initiative>) {
  if (!formFields.initiativeName || formFields.initiativeName === "") {
    return {
      initiativeName: REQUIRED_ERROR,
    };
  }

  return {};
}

export function validateFormFields(
  formFields: Partial<Initiative>,
  isCategoryMandatory: boolean
): FormFieldErrors {
  if (formFields.initiativePhase === "L0 - Idea") {
    return validateFormFieldsForDraft(formFields);
  }
  const errors = {
    ...validateInitiativeSection(formFields),
    ...validateScopeSection(formFields, isCategoryMandatory),
    ...validateOwnershipSection(formFields),
    ...validateAdditionalInfoSection(formFields),
    ...validateEnablerSection(formFields),
    ...validateImpactTab(formFields),
  } satisfies FormFieldErrors;

  return errors;
}

function validateInitiativeSection({
  initiativeName,
  initiativeDescription,
  initiativePhase,
}: Partial<Initiative>): FormFieldErrors {
  const errors: FormFieldErrors = {};

  if (!initiativeName) {
    errors.initiativeName = REQUIRED_ERROR;
  } else if (initiativeName.length > 64) {
    errors.initiativeName = getMaxLengthError("Initiative Name", 64);
  }
  if (!initiativeDescription) {
    errors.initiativeDescription = REQUIRED_ERROR;
  } else if (initiativeDescription && initiativeDescription.length > 2048) {
    errors.initiativeDescription = getMaxLengthError("Description", 2048);
  }

  if (!initiativePhase) {
    errors.initiativePhase = REQUIRED_ERROR;
  }

  return errors;
}

function validateScopeSection(
  {
    affectedTeam,
    itChangeRequired,
    itProjectId,
    oeProgram,
    oeProgramNr,
    primaryFunction,
    primarySubFunction,
    primaryLineOfBusiness,
    primaryLineOfBusinessOther,
    commercialLineOfBusiness,
    secondaryLineOfBusiness,
    secondaryLineOfBusinessOther,
    initiativeSponsors,
    category,
  }: Partial<Initiative>,
  isCategoryMandatory: boolean
): FormFieldErrors {
  const errors: FormFieldErrors = {};

  if (itChangeRequired === true && (!itProjectId || itProjectId.length === 0)) {
    errors.itProjectId = REQUIRED_ERROR;
  }

  if (oeProgram === true && (!oeProgramNr || oeProgramNr.length === 0)) {
    errors.oeProgramNr = REQUIRED_ERROR;
  }

  if (affectedTeam && affectedTeam.length > 128) {
    errors.affectedTeam = getMaxLengthError("Affected Team", 128);
  }

  if (!primaryFunction || primaryFunction.length === 0) {
    errors.primaryFunction = REQUIRED_ERROR;
  }

  if (primarySubFunction && primarySubFunction.length > 128) {
    errors.primarySubFunction = getMaxLengthError("Primary Subfunction", 128);
  }

  if (!primaryLineOfBusiness) {
    errors.primaryLineOfBusiness = REQUIRED_ERROR;
  }

  if (primaryLineOfBusiness === "Other" && !primaryLineOfBusinessOther) {
    errors.primaryLineOfBusinessOther = REQUIRED_ERROR;
  }

  if (primaryLineOfBusiness === "Commercial" && !commercialLineOfBusiness) {
    errors.commercialLineOfBusiness = REQUIRED_ERROR;
  }

  if (
    secondaryLineOfBusiness?.includes("Other") &&
    !secondaryLineOfBusinessOther
  ) {
    errors.secondaryLineOfBusinessOther = REQUIRED_ERROR;
  }

  if (initiativeSponsors && initiativeSponsors.length > 128) {
    errors.initiativeSponsors = getMaxLengthError("Initiative Sponsors", 128);
  }

  if (isCategoryMandatory && !category) {
    errors.category = REQUIRED_ERROR;
  }

  return errors;
}

function validateOwnershipSection({
  initiativeOwner,
  implementationProgram,
  implementationProgramOther,
}: Partial<Initiative>): FormFieldErrors {
  const errors: FormFieldErrors = {};

  if (!implementationProgram) {
    errors.implementationProgram = REQUIRED_ERROR;
  }

  if (implementationProgram === "Other" && !implementationProgramOther) {
    errors.implementationProgramOther = REQUIRED_ERROR;
  }

  if (!initiativeOwner || initiativeOwner.length === 0) {
    errors.initiativeOwner = REQUIRED_ERROR;
  }

  return errors;
}

function validateAdditionalInfoSection({
  relatedKPIDashboardHyperlink,
  localInitiativeCluster,
}: Partial<Initiative>): FormFieldErrors {
  const errors: FormFieldErrors = {};

  if (
    relatedKPIDashboardHyperlink &&
    relatedKPIDashboardHyperlink.length > 512
  ) {
    errors.relatedKPIDashboardHyperlink = getMaxLengthError(
      "Related KPI Dashboard Hyperlink",
      512
    );
  }

  if (localInitiativeCluster && localInitiativeCluster.length > 128) {
    errors.localInitiativeCluster = getMaxLengthError(
      "Local Initiative Cluster",
      128
    );
  }

  return errors;
}

function validateEnablerSection({
  initiativeEnablers,
  automationComplexity,
  automationInvolvedSystem,
  automationMethodology,
  automationMethodologyOther,
  automationTechnicalSolution,
  automationSolutionImplementationProvider,
  automationUnderlyingPlatform,
  processMiningEnablementType,
  processMiningEnablementTypeOther,
}: Partial<Initiative>) {
  const errors: FormFieldErrors = {};

  if (initiativeEnablers?.includes("Automation")) {
    if (!automationComplexity) {
      errors.automationComplexity = REQUIRED_ERROR;
    }
    if (!automationInvolvedSystem) {
      errors.automationInvolvedSystem = REQUIRED_ERROR;
    }
    if (!automationMethodology?.length) {
      errors.automationMethodology = REQUIRED_ERROR;
    }
    if (
      automationMethodology?.includes("Other") &&
      (!automationMethodologyOther || automationMethodologyOther.length === 0)
    ) {
      errors.automationMethodologyOther = REQUIRED_ERROR;
    }
    if (!automationTechnicalSolution) {
      errors.automationTechnicalSolution = REQUIRED_ERROR;
    }
    if (!automationSolutionImplementationProvider) {
      errors.automationSolutionImplementationProvider = REQUIRED_ERROR;
    }
    if (
      !automationUnderlyingPlatform ||
      automationUnderlyingPlatform.length === 0
    ) {
      errors.automationUnderlyingPlatform = REQUIRED_ERROR;
    }
  }
  if (initiativeEnablers?.includes("Process Mining")) {
    if (!processMiningEnablementType?.length) {
      errors.processMiningEnablementType = REQUIRED_ERROR;
    }

    if (
      processMiningEnablementType?.includes("Other") &&
      !processMiningEnablementTypeOther
    ) {
      errors.processMiningEnablementTypeOther = REQUIRED_ERROR;
    }
  }

  return errors;
}

function validateImpactTab(formFields: Partial<Initiative>) {
  const errors: FormFieldErrors = {};
  const {
    startDate,
    endDate,
    monitoringEndDate,
    primaryImpactDriver,
    qualitativeImpactType,
    qualitativeImpactTypeOther,
    signOffCoo,
    signOffCfo,
    signOffChro,
    baselineYear,
    tieServiceExpensesIncludesHrCosts,
  } = formFields;

  if (!startDate) {
    errors.startDate = REQUIRED_ERROR;
  }

  if (!endDate) {
    errors.endDate = REQUIRED_ERROR;
  }

  if (!monitoringEndDate) {
    errors.monitoringEndDate = REQUIRED_ERROR;
  }

  const startDateDayjs = dayjs(startDate);
  const endDateDayjs = dayjs(endDate);
  const monitoringEndDateDayjs = dayjs(monitoringEndDate);

  if (isBefore(endDateDayjs, startDateDayjs)) {
    errors.endDate = {
      type: "value",
      message: "The End Date must be after the Start Date",
    };
  }

  if (isBefore(monitoringEndDateDayjs, startDateDayjs)) {
    errors.monitoringEndDate = {
      type: "value",
      message: "The Monitoring End Date must be after the Start Date",
    };
  }

  if (isAfter(endDateDayjs, monitoringEndDateDayjs)) {
    errors.endDate = {
      type: "value",
      message:
        "The End Date must be the same as or before the Monitoring End Date",
    };
  }

  if (!primaryImpactDriver || !primaryImpactDriver.length) {
    errors.primaryImpactDriver = REQUIRED_ERROR;
  }

  if (primaryImpactDriver?.includes("Qualitative") && !qualitativeImpactType) {
    errors.qualitativeImpactType = REQUIRED_ERROR;
  }

  if (qualitativeImpactType === "Other" && !qualitativeImpactTypeOther) {
    errors.qualitativeImpactTypeOther = REQUIRED_ERROR;
  }

  if (!baselineYear) {
    errors.baselineYear = REQUIRED_ERROR;
  }

  if (!signOffCoo) {
    errors.signOffCoo = REQUIRED_ERROR;
  }
  if (!signOffCfo) {
    errors.signOffCfo = REQUIRED_ERROR;
  }
  if (!signOffChro) {
    errors.signOffChro = REQUIRED_ERROR;
  }

  for (const series of formFields.applicableImpactSeries || []) {
    const seriesKey = (series + "Planned") as keyof InitiativeImpactSeries;

    if (
      getMissingImpactValues(
        formFields[seriesKey] as MonthlyImpactMap,
        formFields
      ).length > 0
    ) {
      errors[seriesKey] = {
        type: "required",
        message: "All impact values are required",
      };

      continue;
    }
  }

  if (
    tieServiceExpensesIncludesHrCosts &&
    getMissingImpactValues(
      formFields["hrCostImpactPlanned"] as MonthlyImpactMap,
      formFields
    ).length > 0
  ) {
    errors["hrCostImpactPlanned"] = {
      type: "required",
      message: "All impact values are required",
    };
  }

  return errors;
}

export function validateImpactActuals(
  formFields: Partial<Initiative>,
  allowMissingImpactValues = false
) {
  const errors: FormFieldErrors & {
    fieldsWithErrors?: { year: string; month: number }[];
  } = {};

  for (const series of formFields.applicableImpactSeries || []) {
    const seriesActual = series + "Actual";
    const missingImpactValues = getMissingImpactValues(
      formFields[
        seriesActual as keyof InitiativeImpactSeries
      ] as MonthlyImpactMap,
      formFields
    );

    if (!allowMissingImpactValues && missingImpactValues.length > 0) {
      errors[seriesActual as keyof InitiativeImpactSeries] = {
        type: "required",
        message: "All impact values are required",
        fieldsWithErrors: missingImpactValues,
      } as FieldError & {
        fieldsWithErrors?: { year: string; month: number }[];
      };

      continue;
    }
  }

  return errors;
}

function isBefore(date1: Dayjs, date2: Dayjs) {
  return (
    date1.year() < date2.year() ||
    (date1.year() === date2.year() && date1.month() < date2.month())
  );
}

function isAfter(date1: Dayjs, date2: Dayjs) {
  return (
    date1.year() > date2.year() ||
    (date1.year() === date2.year() && date1.month() > date2.month())
  );
}
